import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { DEFAULT_LANGUAGE, LANGUAGES, STORAGE_KEY } from "./config";

import enNavbar from "./en/navbar.json";
import enHome from "./en/home.json";
import enAbout from "./en/about.json";
import enCertifications from "./en/certifications.json";
import enProjects from "./en/projects.json";
import enExperience from "./en/experience.json";
import enFooter from "./en/footer.json";
import enResume from "./en/resume.json";
import enTerminal from "./en/terminal.json";

import jaNavbar from "./ja/navbar.json";
import jaHome from "./ja/home.json";
import jaAbout from "./ja/about.json";
import jaCertifications from "./ja/certifications.json";
import jaProjects from "./ja/projects.json";
import jaExperience from "./ja/experience.json";
import jaFooter from "./ja/footer.json";
import jaResume from "./ja/resume.json";
import jaTerminal from "./ja/terminal.json";

export const translations = {
  en: {
    navbar: enNavbar,
    home: enHome,
    about: enAbout,
    certifications: enCertifications,
    projects: enProjects,
    experience: enExperience,
    footer: enFooter,
    resume: enResume,
    terminal: enTerminal,
  },
  ja: {
    navbar: jaNavbar,
    home: jaHome,
    about: jaAbout,
    certifications: jaCertifications,
    projects: jaProjects,
    experience: jaExperience,
    footer: jaFooter,
    resume: jaResume,
    terminal: jaTerminal,
  },
};

const LanguageContext = createContext(null);

function getNestedValue(obj, path) {
  return path.split(".").reduce((acc, key) => {
    if (acc === undefined || acc === null) return undefined;
    return acc[key];
  }, obj);
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved && translations[saved] ? saved : DEFAULT_LANGUAGE;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((lang) => {
    if (translations[lang]) {
      setLanguageState(lang);
    }
  }, []);

  const t = useCallback(
    (key) => {
      const value = getNestedValue(translations[language], key);
      return value !== undefined ? value : key;
    },
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, languages: LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useTranslation must be used within LanguageProvider");
  }
  return context;
}

export { LANGUAGES };
